/**
 * CHẨN ĐOÁN CAMERA — vì sao không mở được, nói bằng tiếng Việt.
 *
 * Lý do tách khỏi component: thông điệp sai làm mất hàng giờ. Ngày
 * 11/09/2026, `ChupKhuonMat` gặp `navigator.mediaDevices === undefined` thì
 * chuyển sang trạng thái 'khong-co-camera' nhưng QUÊN đặt biến `loi`, nên
 * nhân viên thấy một hộp lỗi TRỐNG RỖNG. Nguyên nhân thật là trang đang chạy
 * trên `http://` — và không một chữ nào nói ra điều đó.
 *
 * Quy tắc: mỗi nhánh lỗi phải trả về (a) chuyện gì xảy ra, (b) LÀM GÌ TIẾP.
 * Không có nhánh nào được phép trả chuỗi rỗng.
 */

export type LyDoCamera =
  | 'ok'
  | 'khong_bao_mat'      // không phải secure context — nguyên nhân phổ biến nhất
  | 'trinh_duyet_cu'     // secure context nhưng không có mediaDevices
  | 'chua_cap_quyen'     // NotAllowedError
  | 'bi_chan_chinh_sach' // SecurityError / Permissions-Policy
  | 'khong_co_thiet_bi'  // NotFoundError
  | 'dang_ban'           // NotReadableError — app khác đang giữ camera
  | 'khong_dap_ung'      // OverconstrainedError
  | 'bi_huy'             // AbortError
  | 'khong_ro'

export interface ChanDoanCamera {
  ly_do: LyDoCamera
  /** Câu hiện cho nhân viên. KHÔNG BAO GIỜ rỗng. */
  thong_diep: string
  /** Dòng kỹ thuật để nhân viên đọc cho quản trị khi báo lỗi. */
  chi_tiet: string
}

/** Trang có đang chạy trong "secure context" không.
 *
 *  Trình duyệt chỉ mở `navigator.mediaDevices` (camera), `geolocation`,
 *  `crypto.subtle` và `crypto.randomUUID` khi ĐÚNG một trong hai:
 *    · giao thức `https:`
 *    · máy chủ là `localhost` / `127.0.0.1`
 *
 *  `http://192.168.1.110:3100` KHÔNG thuộc ngoại lệ localhost. Safari trên
 *  iPhone lại không có cờ nào để bỏ qua. Xem docs/13 §13.4.
 */
export function laNguCanhBaoMat(): boolean {
  if (typeof window === 'undefined') return false
  // `isSecureContext` có mặt trên mọi trình duyệt còn được hỗ trợ; phòng hờ
  // thì suy ra từ giao thức.
  if (typeof window.isSecureContext === 'boolean') return window.isSecureContext
  const l = window.location
  return l.protocol === 'https:' || l.hostname === 'localhost' || l.hostname === '127.0.0.1'
}

function moTaDiaChi(): string {
  if (typeof window === 'undefined') return ''
  return `${window.location.protocol}//${window.location.host}`
}

/**
 * Kiểm tra TRƯỚC khi gọi `getUserMedia`.
 *
 * Gọi sớm để màn hình nói được lý do ngay lúc mở, thay vì để nhân viên bấm
 * nút rồi mới thấy hỏng.
 */
export function kiemTraTruoc(): ChanDoanCamera {
  if (!laNguCanhBaoMat()) {
    return {
      ly_do: 'khong_bao_mat',
      thong_diep:
        'Trình duyệt yêu cầu kết nối bảo mật (HTTPS) mới cho phép dùng camera. '
        + `Trang này đang mở bằng ${moTaDiaChi()}. Hãy mở lại bằng địa chỉ bắt `
        + 'đầu bằng https:// — hỏi quản trị đường dẫn HTTPS của công ty.',
      chi_tiet: `isSecureContext=false · origin=${moTaDiaChi()}`,
    }
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return {
      ly_do: 'trinh_duyet_cu',
      thong_diep:
        'Trình duyệt này không hỗ trợ mở camera từ trang web. Dùng Chrome '
        + '(Android) hoặc Safari (iPhone) bản mới, hoặc báo quản trị.',
      chi_tiet: `mediaDevices=undefined · UA=${navigator.userAgent.slice(0, 80)}`,
    }
  }
  return { ly_do: 'ok', thong_diep: '', chi_tiet: '' }
}

/** Lỗi do `getUserMedia` ném ra → chẩn đoán. */
export function chanDoanLoi(e: unknown): ChanDoanCamera {
  const ten = e instanceof DOMException ? e.name : ''
  const mo_ta = e instanceof Error ? e.message : String(e)
  const chi_tiet = `${ten || 'UnknownError'}: ${mo_ta}`.slice(0, 200)

  switch (ten) {
    case 'NotAllowedError':
      // Cũng là lỗi trả về khi người dùng bấm "Chặn" ở hộp thoại xin quyền.
      return {
        ly_do: 'chua_cap_quyen',
        thong_diep:
          'Bạn chưa cho phép dùng camera. Bấm vào biểu tượng ổ khoá bên trái '
          + 'thanh địa chỉ → Quyền → bật Camera, rồi tải lại trang.',
        chi_tiet,
      }
    case 'SecurityError':
      return {
        ly_do: 'bi_chan_chinh_sach',
        thong_diep:
          'Trình duyệt chặn camera trên trang này vì lý do bảo mật. Thường là '
          + 'do trang chưa chạy HTTPS. Báo quản trị kèm dòng chi tiết bên dưới.',
        chi_tiet,
      }
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return {
        ly_do: 'khong_co_thiet_bi',
        thong_diep: 'Không tìm thấy camera nào trên thiết bị này.',
        chi_tiet,
      }
    case 'NotReadableError':
    case 'TrackStartError':
      return {
        ly_do: 'dang_ban',
        thong_diep:
          'Camera đang được ứng dụng khác sử dụng. Đóng Zalo, Messenger, '
          + 'Camera… rồi thử lại.',
        chi_tiet,
      }
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return {
        ly_do: 'khong_dap_ung',
        thong_diep:
          'Camera của máy không đáp ứng được yêu cầu về độ phân giải. Đang '
          + 'thử lại với thiết lập đơn giản hơn.',
        chi_tiet,
      }
    case 'AbortError':
      return {
        ly_do: 'bi_huy',
        thong_diep: 'Việc mở camera bị gián đoạn. Thử lại giúp bạn.',
        chi_tiet,
      }
    default:
      return {
        ly_do: 'khong_ro',
        thong_diep:
          'Không mở được camera. Thử tải lại trang; nếu vẫn vậy, báo quản trị '
          + 'kèm dòng chi tiết bên dưới.',
        chi_tiet,
      }
  }
}
